import { LidarInventoryDataEntryPlanningOrchestratorService } from 'orchestrator';
import { InventorySubTypeService, TeamService } from 'cbd-proxies';
import { ActionDeleteMeasurement } from '../inventory-data-entry/action-delete-measurement/action-delete-measurement';
import { ActionUndoRoutine } from './action-undo-routine/action-undo-routine';
import {
  ActionOverRecordTypes,
  AdditionalGridColumnDefinition,
  FieldDefinition,
  FieldBuilder,
  MenuCategories,
  MenuSubcategories,
  MENU_CATEGORY_KEY,
  MENU_SUBCATEGORY_KEY,
  MENU_NAME_SUFFIX,
  ZendraLocalizationServices,
  IModal,
  createFormModalConfig,
  createLocalizationKey,
  IActionsSchema,
  modalAction,
  triggerAction,
} from '@zendra/shared';
import { Catalog } from '../inventory-data-entry/services/catalog';
import { ActionRunRoutine } from './action-run-routine/action-run-routine';

FieldBuilder.setNamespace('Inventory');

const buildReadOnlyGridColumn = (
  key: string,
  label: string,
): AdditionalGridColumnDefinition => ({
  key,
  label,
  hiddenByDefault: true,
  column: {
    field: key,
    sortable: false,
    filterable: false,
    minWidth: 140,
    params: {
      excludeFromExport: true,
    },
  },
});

const standNumberField = FieldBuilder
  .text('standNumber', 'Entity.LidarInventoryDataEntry.StandNumber')
  .maxLength(10)
  .build();

standNumberField.additionalGridColumns = [
  buildReadOnlyGridColumn('standId', 'Stand ID'),
];

export const LIDARINVENTORYDATAENTRY_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .date('inventoryDate', 'Entity.LidarInventoryDataEntry.InventoryDate')
    .build(),
  standNumberField,
  FieldBuilder.foreignKey('teamId', 'Entity.Team', TeamService)
    .display('name')
    .displayFromNested('teamName')
    .sortBy('teamName')
    .gridColumnMinWidth(140)
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('inventorySubTypeId', 'Entity.InventorySubType', InventorySubTypeService)
    .display('name')
    .displayFromNested('inventorySubTypeName')
    .sortBy('inventorySubTypeName')
    .gridColumnMinWidth(140)
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder
    .number('plotCount', 'Entity.LidarInventoryDataEntry.PlotCount')
    .build(),
  FieldBuilder
    .number('treeCount', 'Entity.LidarInventoryDataEntry.TreeCount')
    .build(),
  FieldBuilder
    .number('averageDiameter', 'Entity.LidarInventoryDataEntry.AverageDiameter')
    .decimal()
    .build(),
  FieldBuilder
    .number('radius', 'Entity.LidarInventoryDataEntry.Radius')
    .decimal()
    .build(),
  FieldBuilder
    .number('area', 'Entity.LidarInventoryDataEntry.Area')
    .decimal()
    .build(),
  FieldBuilder
    .number('averageVolume', 'Entity.LidarInventoryDataEntry.AverageVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('treeVolume', 'Entity.LidarInventoryDataEntry.TreeVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('basalArea', 'Entity.LidarInventoryDataEntry.BasalArea')
    .decimal()
    .build(),
  FieldBuilder
    .number('dominantHeight', 'Entity.LidarInventoryDataEntry.DominantHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('maximumHeight', 'Entity.LidarInventoryDataEntry.MaximumHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('averageHeight', 'Entity.LidarInventoryDataEntry.AverageHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('individualVolume', 'Entity.LidarInventoryDataEntry.IndividualVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('treeAge', 'Entity.LidarInventoryDataEntry.TreeAge')
    .decimal()
    .build(),
  FieldBuilder
    .number('error', 'Entity.LidarInventoryDataEntry.Error')
    .decimal()
    .build(),
  FieldBuilder
    .number('minimumVolume', 'Entity.LidarInventoryDataEntry.MinimumVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('maximumVolume', 'Entity.LidarInventoryDataEntry.MaximumVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('meditionNumber', 'Entity.LidarInventoryDataEntry.MeditionNumber')
    .build(),
  FieldBuilder
    .checkbox('isProcessed', 'Entity.LidarInventoryDataEntry.IsProcessed')
    .build(),
  FieldBuilder
    .text('finalDecision_T2_T4', 'Entity.LidarInventoryDataEntry.FinalDecision_T2_T4')
    .maxLength(50)
    .build(),
  FieldBuilder
    .number('dab', 'Entity.LidarInventoryDataEntry.Dab')
    .decimal()
    .build(),
  FieldBuilder
    .number('t2PlusT4_10Pct', 'Entity.LidarInventoryDataEntry.T2PlusT4_10Pct')
    .decimal()
    .build(),
  FieldBuilder
    .number('t2PlusT4_20Pct', 'Entity.LidarInventoryDataEntry.T2PlusT4_20Pct')
    .decimal()
    .build(),
  FieldBuilder
    .number('volume_10Pct', 'Entity.LidarInventoryDataEntry.Volume_10Pct')
    .decimal()
    .build(),
  FieldBuilder
    .number('volume_20Pct', 'Entity.LidarInventoryDataEntry.Volume_20Pct')
    .decimal()
    .build(),
  FieldBuilder
    .number('linew_Min', 'Entity.LidarInventoryDataEntry.Linew_Min')
    .decimal()
    .build(),
  FieldBuilder
    .number('linew_Mean', 'Entity.LidarInventoryDataEntry.Linew_Mean')
    .decimal()
    .build(),
  FieldBuilder
    .number('linew_Max', 'Entity.LidarInventoryDataEntry.Linew_Max')
    .decimal()
    .build(),
];

const namespace = ZendraLocalizationServices.Inventory;

export const ACTION_IDS = {
  importExcelModal: 0,
  downloadExcelTemplateTrigger: 1,
  deleteMeasurement: 2,
  runLidarInventoryRoutine: 3,
  undoLidarInventoryRoutine: 4,
} as const;

export const LIDAR_INVENTORY_DATA_ENTRY_ACTION_IDS = ACTION_IDS;

const ACTION_DELETE_MEASUREMENT_WRAPPER: IModal = createFormModalConfig({
  component: ActionDeleteMeasurement,
  title: createLocalizationKey('Action.Modal.DeleteMeasurementLidarInventoryDataEntry.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.LidarInventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    deleteService: LidarInventoryDataEntryPlanningOrchestratorService,
    catalogService: Catalog,
    measurementLabelKey: createLocalizationKey('Entity.LidarInventoryDataEntry.MeditionNumberDelete', namespace),
    successMessageKey: 'Actions:DeleteMeasurementLidarInventoryDataEntrySuccess',
    useLidar: true,
  },
});

const ACTION_UNDO_ROUTINE_WRAPPER: IModal = createFormModalConfig({
  component: ActionUndoRoutine,
  title: createLocalizationKey('Action.Modal.UndoLidarInventoryRoutine.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.LidarInventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    deleteService: LidarInventoryDataEntryPlanningOrchestratorService,
    catalogService: Catalog,
    measurementLabelKey: createLocalizationKey('Entity.LidarInventoryDataEntry.MeditionNumberDelete', namespace),
    successMessageKey: 'Actions:UndoLidarInventorySuccess',
  },
});

const ACTION_RUN_ROUTINE_WRAPPER: IModal = createFormModalConfig({
  component: ActionRunRoutine,
  title: createLocalizationKey('Action.Modal.RunLidarInventoryDataEntryRoutine.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.LidarInventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    runRoutineService: LidarInventoryDataEntryPlanningOrchestratorService,
    catalogService: {
      useFactory: (catalog: Catalog) => {
        catalog.useLidar = true;
        return catalog;
      },
      deps: [Catalog],
    },
    measurementLabelKey: createLocalizationKey('Entity.LidarInventoryDataEntry.MeditionNumberToProcess', namespace),
    successMessageKey: 'Actions:RunLidarInventoryDataEntrySuccess',
    isRunRoutineMode: true,
    useLidar: true,
  },
});

export const LIDARINVENTORYDATAENTRY_ACTIONS: IActionsSchema = {
  overTable: [
    triggerAction(
      ACTION_IDS.importExcelModal,
      'Action.Modal.ImportMeasurementLidarInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.Inventory,
    ),
    triggerAction(
      ACTION_IDS.downloadExcelTemplateTrigger,
      'Actions:ExcelTemplateDownload',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.LanguageService,
    ),
    modalAction(
      ACTION_IDS.deleteMeasurement,
      'Action.Modal.DeleteMeasurementLidarInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ACTION_DELETE_MEASUREMENT_WRAPPER,
      namespace
    ),
    modalAction(
      ACTION_IDS.runLidarInventoryRoutine,
      'Action.Modal.RunLidarInventoryDataEntryRoutine',
      ActionOverRecordTypes.OverTable,
      ACTION_RUN_ROUTINE_WRAPPER,
      namespace
    ),
    modalAction(
      ACTION_IDS.undoLidarInventoryRoutine,
      'Action.Modal.UndoLidarInventoryRoutine',
      ActionOverRecordTypes.OverTable,
      ACTION_UNDO_ROUTINE_WRAPPER,
      namespace
    ),
  ],
};

export const LIDAR_INVENTORY_DATA_ENTRY_ACTIONS_CONFIG = LIDARINVENTORYDATAENTRY_ACTIONS;

export const LIDARINVENTORYDATAENTRY_CONFIG = {
  entityName: 'LidarInventoryDataEntry',
  localizationKey: 'Inventory::Entity.LidarInventoryDataEntry',
  fields: LIDARINVENTORYDATAENTRY_FIELDS,
  permission: 'PlanningInventory.LidarInventoryDataEntries',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Inventory + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.TransactionsInventory + MENU_NAME_SUFFIX,
  actions: LIDAR_INVENTORY_DATA_ENTRY_ACTIONS_CONFIG,
  actionIds: LIDAR_INVENTORY_DATA_ENTRY_ACTION_IDS,

};
